/**
 * Placement status abbreviations for compact UI display.
 * Key: `${activityTypeId}-${activityStatusId}`. Tooltip shows full description.
 */
const STATUS_BY_IDS: Record<string, { abbr: string; full: string }> = {
  '1-1': { abbr: 'WFC', full: 'Waiting for Clearance' },
  '1-2': { abbr: 'RFO', full: 'Request File Out' },
  '1-3': { abbr: 'S', full: 'Sent' },
  '1-4': { abbr: 'DBT', full: 'Declined by Traveler' },
  '1-5': { abbr: 'DBF', full: 'Declined by Facility' },
  '1-6': { abbr: 'I', full: 'Interviewed' },
  '1-7': { abbr: 'RC', full: 'Request Cancelled' },
  '1-8': { abbr: 'OKTS', full: 'OK to Send' },
  '1-9': { abbr: 'OBF', full: 'Offer by Facility' },
  '1-10': { abbr: 'OBBT', full: 'Offer Booked by Traveler' },
  '1-16': { abbr: 'IS', full: 'Interview Scheduled' },
  '3-4': { abbr: 'DBT', full: 'Declined by Traveler' },
  '3-5': { abbr: 'DBF', full: 'Declined by Facility' },
  '3-7': { abbr: 'RC', full: 'Request Cancelled' },
  '3-9': { abbr: 'OBF', full: 'Offer by Facility' },
  '3-10': { abbr: 'OBBT', full: 'Offer Booked by Traveler' },
  '3-11': { abbr: 'TA', full: 'Traveler Accepts' },
  '3-12': { abbr: 'FC', full: 'Facility Confirms' },
  '4-13': { abbr: 'B', full: 'Booked' },
  '4-14': { abbr: 'BA', full: 'Booking Approved' },
  '4-15': { abbr: 'WGTG', full: 'Waiting Good To Go' },
  '4-17': { abbr: 'PPW', full: 'Postponement Warning' },
  '4-18': { abbr: 'GTG', full: 'Good To Go' },
  '4-20': { abbr: 'CBT', full: 'Canceled By Traveler' },
  '4-21': { abbr: 'CBF', full: 'Canceled By Facility' },
  '4-22': { abbr: 'CBA', full: 'Canceled by AMH' },
  '4-23': { abbr: 'OA', full: 'On Assignment' },
  '4-28': { abbr: 'CBS', full: 'Canceled by Settlement' },
  '5-23': { abbr: 'OA', full: 'On Assignment' },
  '5-24': { abbr: 'AC', full: 'Assignment Complete' },
  '5-25': { abbr: 'CBT', full: 'Canceled By Traveler' },
  '5-26': { abbr: 'CBF', full: 'Canceled By Facility' },
  '5-27': { abbr: 'CBA', full: 'Canceled by AMH' },
  '5-29': { abbr: 'CBS', full: 'Canceled by Settlement' },
};

/** Normalized description -> { abbr, full } for fallback when IDs not available */
const DESCRIPTION_MAP: Record<string, { abbr: string; full: string }> = {};
Object.values(STATUS_BY_IDS).forEach(v => {
  const key = v.full.toLowerCase().replace(/\s+/g, ' ').trim();
  if (!DESCRIPTION_MAP[key]) DESCRIPTION_MAP[key] = v;
});

export interface StatusDisplay {
  abbr: string;
  full: string;
}

/**
 * Returns display values for placement status. Uses abbreviation for UI, full for tooltip.
 * Internal logic continues to use PlacementRecord.status (active/completed/pending/cancelled) for colors/filters.
 */
export function getStatusDisplay(
  activityTypeId?: number,
  activityStatusId?: number,
  statusLabel?: string,
  fallbackStatus?: string,
): StatusDisplay {
  if (activityTypeId != null && activityStatusId != null) {
    const entry = STATUS_BY_IDS[`${activityTypeId}-${activityStatusId}`];
    if (entry) return entry;
  }
  if (statusLabel) {
    const key = statusLabel.toLowerCase().replace(/\s+/g, ' ').trim();
    const entry = DESCRIPTION_MAP[key];
    if (entry) return entry;
    const partial = Object.entries(DESCRIPTION_MAP).find(([k]) => key.includes(k) || k.includes(key));
    if (partial) return partial[1];
  }
  return {
    abbr: fallbackStatus || '—',
    full: statusLabel || fallbackStatus || '—',
  };
}
